import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandExtraction } from "../components/brand/BrandExtraction";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { useBrand } from "../hooks/useBrand";
import { useStartPipeline } from "../hooks/useContent";
import { useCreditBalance, useSubscribe } from "../hooks/useCredits";

const TOTAL_STEPS = 4;

// ── Progress Indicator ──

function ProgressDots({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-center gap-3">
      {Array.from({ length: total }).map((_, i) => {
        const step = i + 1;
        const isCompleted = step < current;
        const isActive = step === current;

        return (
          <span
            key={step}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
              isCompleted
                ? "bg-brand-600 text-white"
                : isActive
                  ? "bg-brand-600 text-white"
                  : "border-2 border-slate-300 text-slate-400"
            }`}
            aria-label={
              isCompleted
                ? `Step ${step} completed`
                : isActive
                  ? `Step ${step} active`
                  : `Step ${step} upcoming`
            }
          >
            {isCompleted ? (
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              step
            )}
          </span>
        );
      })}
    </div>
  );
}

// ── Step 1: Brand Setup ──

function BrandSetupStep({
  onComplete,
  onSkip,
}: {
  onComplete: (brandId: string) => void;
  onSkip: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Let's set up your brand
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Paste your website URL and we'll extract your brand identity
          automatically.
        </p>
      </div>
      <BrandExtraction onComplete={onComplete} />
      <div className="text-center">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Review Brand ──

function ReviewBrandStep({
  brandId,
  onNext,
  onBack,
  onOpenAgent,
}: {
  brandId: string | null;
  onNext: () => void;
  onBack: () => void;
  /** PRD-0032 Phase 6 — jump to `/agent/:brandId` without finishing the wizard */
  onOpenAgent?: () => void;
}) {
  const { data: brandData, isLoading } = useBrand(brandId ?? undefined);
  const brand = brandData?.data;

  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Populate fields once brand data loads
  if (brand && !initialized) {
    setName(brand.name ?? "");
    setIndustry(brand.industry ?? "");
    setTargetAudience(brand.target_audience ?? "");
    setInitialized(true);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Review your brand profile
        </h2>
        <p className="text-sm text-slate-500">
          No brand found. You can set one up later from the Brand page.
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button onClick={onNext}>Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Review your brand profile
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Make any adjustments to your brand details.
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Brand Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your brand name"
        />
        <Input
          label="Industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Technology, Health, Education"
        />
        <Input
          label="Target Audience"
          value={targetAudience}
          onChange={(e) => setTargetAudience(e.target.value)}
          placeholder="Who are you trying to reach?"
        />
      </div>

      <div className="flex justify-center gap-3">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext}>Looks good</Button>
      </div>

      {brandId && onOpenAgent ? (
        <div className="text-center">
          <button
            type="button"
            onClick={onOpenAgent}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Open marketing agent chat
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Step 3: Connect Platforms ──
//
// Account connections are real OAuth flows owned by Settings → Connections
// (LinkedIn, Bluesky, Instagram, Facebook Pages, Google). Each provider's
// callback lands on that page, so this step opens it in a new tab and keeps
// onboarding here. It never reports a connection itself: the only place a
// "Connected" state is shown is the Connections page, from the backend.

export function onboardingConnectionsHref(brandId: string | null): string {
  return `/settings/connections${brandId ? `?brandId=${encodeURIComponent(brandId)}` : ""}`;
}

export function ConnectPlatformsStep({
  brandId,
  onNext,
  onSkip,
}: {
  brandId: string | null;
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Connect your platforms
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Connect the accounts where you want to publish content.
        </p>
      </div>

      <Card>
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            LinkedIn, Bluesky, Instagram, Facebook Pages, and Google are connected
            from Connections. It opens in a new tab so you can finish setup here.
          </p>
          <a
            href={onboardingConnectionsHref(brandId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            Open Connections
          </a>
        </div>
      </Card>

      <div className="flex flex-col items-center gap-2">
        <Button onClick={onNext}>Continue</Button>
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Upgrade Nudge (Sub-Task 13.3) ──

function UpgradeNudge({ onContinue }: { onContinue: () => void }) {
  const { data: creditData } = useCreditBalance();
  const subscribe = useSubscribe();
  const balance = creditData?.data;
  const credits = balance?.credits ?? 0;

  async function handleUpgrade() {
    try {
      const result = await subscribe.mutateAsync({
        success_url: `${window.location.origin}/onboarding?upgraded=true`,
        cancel_url: `${window.location.origin}/onboarding`,
      });
      const checkoutUrl = result.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch {
      // Error handled by mutation state
    }
  }

  // Only show if credits are low (3 or fewer)
  if (credits > 3) {
    onContinue();
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Ready to unlock more?
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          You have{" "}
          <span className="font-semibold text-slate-700">{credits}</span> of 3
          starter credits. Add monthly credits for $20/month and get 20 credits.
        </p>
      </div>

      <Card className="text-center">
        <p className="text-3xl font-bold text-slate-900">$20</p>
        <p className="text-sm text-slate-500">per month</p>
        <ul className="mt-4 space-y-2 text-left text-sm text-slate-600">
          <li className="flex items-center gap-2">
            <svg
              className="h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            20 content credits per month
          </li>
          <li className="flex items-center gap-2">
            <svg
              className="h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            All 6 AI agents
          </li>
          <li className="flex items-center gap-2">
            <svg
              className="h-4 w-4 flex-shrink-0 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Multi-platform publishing
          </li>
        </ul>
        <div className="mt-6">
          <Button
            onClick={handleUpgrade}
            loading={subscribe.isPending}
            className="w-full"
          >
            Upgrade
          </Button>
        </div>
        {subscribe.isError && (
          <p className="mt-2 text-sm text-red-500">
            Failed to start checkout. Please try again.
          </p>
        )}
      </Card>

      <div className="text-center">
        <button
          type="button"
          onClick={onContinue}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Continue with free plan
        </button>
      </div>
    </div>
  );
}

// ── Step 4: First Check-in ──

function FirstCheckinStep({
  brandId,
  onSkip,
}: {
  brandId: string | null;
  onSkip: () => void;
}) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const startPipeline = useStartPipeline();

  async function handleGenerate() {
    if (!text.trim()) return;
    try {
      const result = await startPipeline.mutateAsync({
        brand_id: brandId ?? "",
        text: text.trim(),
      });
      const runId = result.data?.id;
      if (runId) {
        navigate(`/ideas/${runId}`);
      }
    } catch {
      // Error handled by mutation state
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Try your first content run
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Tell us what you worked on this week and we'll generate content for
          you.
        </p>
      </div>

      <div className="space-y-3">
        <label
          htmlFor="checkin-text"
          className="text-sm font-medium text-slate-700"
        >
          What did you work on this week?
        </label>
        <textarea
          id="checkin-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="We launched a new feature that lets users..."
          rows={5}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {startPipeline.isError && (
        <p className="text-sm text-red-500">
          Failed to generate content. Please try again.
        </p>
      )}

      <div className="flex flex-col items-center gap-2">
        <Button
          onClick={handleGenerate}
          disabled={!text.trim()}
          loading={startPipeline.isPending}
        >
          Generate Content
        </Button>
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-slate-400 hover:text-slate-600"
        >
          Skip to workspace
        </button>
      </div>
    </div>
  );
}

// ── Main Onboarding Page ──

export function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  function openAgentChat() {
    if (brandId) navigate(`/agent/${brandId}`);
  }

  /** PRD-0032 Phase 6 — primary exit lands in agent chat when a brand exists */
  function finishOnboardingToAgent() {
    if (brandId) {
      navigate(`/agent/${brandId}`);
    } else {
      navigate("/home");
    }
  }

  function handleBrandComplete(id: string) {
    setBrandId(id);
    setStep(2);
  }

  function handleStep3Next() {
    // Show upgrade nudge before step 4
    setShowUpgrade(true);
  }

  function handleUpgradeContinue() {
    setShowUpgrade(false);
    setStep(4);
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.10),_transparent_40%),linear-gradient(180deg,_#f8fafc,_#ffffff)] px-4 py-12">
      <div className="w-full max-w-3xl rounded-[32px] border border-slate-200 bg-white/95 p-6 shadow-[0_24px_72px_-28px_rgba(15,23,42,0.25)] backdrop-blur md:p-8">
        <div className="mb-8 text-center">
          <p className="font-data text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500">
            Welcome to HelloConvo
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
            Build your workspace
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Set up your brand, connect distribution surfaces, and run your first content workflow.
          </p>
        </div>

        <div className="mb-10">
          <ProgressDots current={step} total={TOTAL_STEPS} />
        </div>

        {step === 1 && (
          <BrandSetupStep
            onComplete={handleBrandComplete}
            onSkip={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <ReviewBrandStep
            brandId={brandId}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
            onOpenAgent={openAgentChat}
          />
        )}

        {step === 3 && !showUpgrade && (
          <ConnectPlatformsStep
            brandId={brandId}
            onNext={handleStep3Next}
            onSkip={handleStep3Next}
          />
        )}

        {showUpgrade && (
          <UpgradeNudge onContinue={handleUpgradeContinue} />
        )}

        {step === 4 && !showUpgrade && (
          <FirstCheckinStep brandId={brandId} onSkip={finishOnboardingToAgent} />
        )}
      </div>
    </div>
  );
}
